import React, { useState } from "react";
import { Plus, Flag, CalendarDays, Loader2, UserPlus } from "lucide-react";
import { PRIORITIES, PRIORITY_ORDER, useTeamMembers, initialsOf } from "./todoUtils";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function TodoQuickAdd({ onAdd, defaultDue = "", autoFocus = false, placeholder = "Aggiungi un task… (Invio per salvare)" }) {
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("media");
  const [due, setDue] = useState(defaultDue);
  const [showDate, setShowDate] = useState(!!defaultDue);
  const [assigneeId, setAssigneeId] = useState("");
  const [saving, setSaving] = useState(false);
  const members = useTeamMembers();
  const { user } = useAuth();
  const colleagues = members.filter((m) => m.id !== user?.id);
  const assignee = colleagues.find((m) => m.id === assigneeId);

  const submit = async () => {
    const text = title.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await onAdd({ title: text, priority, due: due || null, assigneeId: assigneeId || null });
      setTitle("");
      setAssigneeId("");
      toast.success(assignee ? `Task assegnato a ${assignee.name || assignee.email} 📨` : "Task aggiunto");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const cyclePriority = () => {
    const idx = PRIORITY_ORDER.indexOf(priority);
    setPriority(PRIORITY_ORDER[(idx + 1) % PRIORITY_ORDER.length]);
  };

  const prio = PRIORITIES[priority];

  return (
    <div className="flex items-center gap-2" data-testid="todo-quick-add">
      <div className="flex-1 flex items-center gap-1.5 bg-white border border-slate-200 rounded-[8px] px-2.5 py-1.5 focus-within:border-[#B7955B] focus-within:shadow-[0_0_0_3px_rgba(183,149,91,0.12)] transition-all">
        {/* Toggle priorita' (cicla) */}
        <button
          type="button"
          onClick={cyclePriority}
          title={`Priorità: ${prio.label} (clicca per cambiare)`}
          data-testid="todo-priority-toggle"
          className="p-1 rounded-md hover:bg-slate-100 transition-colors shrink-0"
        >
          <Flag className="w-3.5 h-3.5" strokeWidth={2.5} style={{ color: prio.color }} />
        </button>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          data-testid="todo-quick-add-input"
          className="flex-1 min-w-0 text-[13px] bg-transparent outline-none placeholder:text-slate-400 text-slate-800"
        />
        {/* Assegna a un collega */}
        {colleagues.length > 0 && (
          <div className="relative shrink-0" title={assignee ? `Assegnato a ${assignee.name || assignee.email}` : "Assegna a un collega"}>
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                assignee ? "bg-[#0A192F] text-[#D4AB6E] text-[10px] font-bold" : "text-slate-400 hover:bg-slate-100"
              }`}
            >
              {assignee ? initialsOf(assignee.name || assignee.email) : <UserPlus className="w-3.5 h-3.5" strokeWidth={2.2} />}
            </div>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              data-testid="todo-assignee-select"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              aria-label="Assegna task a"
            >
              <option value="">📌 Per me</option>
              {colleagues.map((m) => (
                <option key={m.id} value={m.id}>{m.name || m.email}</option>
              ))}
            </select>
          </div>
        )}
        {/* Data scadenza compatta */}
        {showDate ? (
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            data-testid="todo-due-input"
            className="text-[11px] bg-transparent outline-none text-slate-600 w-[110px] shrink-0 tabular-nums"
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowDate(true)}
            title="Aggiungi scadenza"
            data-testid="todo-show-due-btn"
            className="p-1 rounded-md text-slate-400 hover:text-[#B7955B] hover:bg-slate-100 transition-colors shrink-0"
          >
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={2.2} />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={!title.trim() || saving}
        data-testid="todo-add-btn"
        className="w-9 h-9 rounded-[8px] bg-[#B7955B] hover:bg-[#A18048] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-all btn-press shrink-0 shadow-[0_2px_6px_rgba(183,149,91,0.35)]"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" strokeWidth={2.5} />}
      </button>
    </div>
  );
}
